import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Plus, QrCode, Image as ImageIcon, Download, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const QRTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/qr-templates`, {
        headers: { 'X-Admin-Token': token }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
      alert('Failed to load QR templates');
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async () => {
    if (!newTemplateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/qr-templates`,
        { name: newTemplateName },
        { headers: { 'X-Admin-Token': token } }
      );
      setNewTemplateName('');
      loadTemplates();
      alert('✅ Template created successfully!');
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Failed to create template');
    } finally {
      setCreating(false);
    }
  };

  const uploadBackground = async (templateId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const token = localStorage.getItem('admin_token');
          await axios.post(
            `${API}/admin/qr-templates/${templateId}/background`,
            { dataUrl: event.target.result },
            { headers: { 'X-Admin-Token': token } }
          );
          loadTemplates();
          alert('✅ Background uploaded successfully!');
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error uploading background:', error);
        alert('Failed to upload background');
      }
    };
    
    input.click();
  };

  const uploadUserImage = async (templateId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const token = localStorage.getItem('admin_token');
          await axios.post(
            `${API}/admin/qr-templates/${templateId}/upload`,
            { dataUrl: event.target.result, circle_index: 0 },
            { headers: { 'X-Admin-Token': token } }
          );
          alert('✅ User image uploaded successfully!');
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error uploading user image:', error);
        alert('Failed to upload user image');
      }
    };
    
    input.click();
  };

  const downloadQRCode = async (templateId, templateName) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${API}/admin/qr-templates/${templateId}/qr`,
        {
          headers: { 'X-Admin-Token': token },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `qr-${templateName}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading QR code:', error);
      alert('Failed to download QR code');
    }
  };

  const downloadPDF = async (templateId, templateName) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${API}/admin/qr-templates/${templateId}/pdf`,
        {
          headers: { 'X-Admin-Token': token },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template-${templateName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${API}/admin/qr-templates/${templateId}`,
        { headers: { 'X-Admin-Token': token } }
      );
      loadTemplates();
      alert('✅ Template deleted successfully!');
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading QR templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">QR Template Manager</h1>
              <p className="text-sm text-gray-500">Create and manage QR code templates</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/admin')}
            >
              ← Back to Admin
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Create New Template */}
        <Card className="p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Template</h2>
          <div className="flex gap-4">
            <input
              type="text"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="Enter template name..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              onKeyPress={(e) => e.key === 'Enter' && createTemplate()}
            />
            <Button
              onClick={createTemplate}
              disabled={creating}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              {creating ? 'Creating...' : 'Create Template'}
            </Button>
          </div>
        </Card>

        {/* Templates Grid */}
        {templates.length === 0 ? (
          <Card className="p-12 text-center">
            <QrCode className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Templates Yet</h3>
            <p className="text-gray-600">Create your first QR template to get started</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card key={template.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="mb-4">
                  {template.background_image ? (
                    <div className="relative group">
                      <img
                        src={template.background_image}
                        alt={template.name}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <Button
                          size="sm"
                          onClick={() => uploadBackground(template.id)}
                          className="bg-white text-gray-900 hover:bg-gray-100"
                        >
                          <ImageIcon className="w-4 h-4 mr-2" />
                          Change
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => uploadBackground(template.id)}
                      className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 transition-colors"
                    >
                      <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">Upload Background</span>
                    </div>
                  )}
                </div>

                <h3 className="font-semibold text-gray-900 mb-4 text-lg">{template.name}</h3>

                <div className="space-y-2">
                  <Button
                    onClick={() => uploadUserImage(template.id)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add User Image
                  </Button>
                  
                  <Button
                    onClick={() => downloadQRCode(template.id, template.name)}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    size="sm"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    Download QR Code
                  </Button>

                  <Button
                    onClick={() => downloadPDF(template.id, template.name)}
                    className="w-full bg-green-600 hover:bg-green-700"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>

                  <Button
                    onClick={() => deleteTemplate(template.id)}
                    className="w-full bg-red-600 hover:bg-red-700"
                    size="sm"
                    variant="outline"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                  
                  <div className="text-xs text-gray-500 text-center pt-2">
                    Created: {new Date(template.created_at).toLocaleDateString()}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QRTemplates;
